// MARK://dhk8/pos/pages/ManagerOverridePage
// Canonical "manager override" surface. Lists the actions that require a manager PIN
// (driven by lib/security-policy) and lets a manager pre-authorize / test the PIN
// (routes through the same usePinGate flow as the in-context prompts). The actual
// gating happens inline at each sensitive handler; this page is the human-readable
// policy + a self-test, not a separate enforcement path.
//
// Also the entry point into "modo entrenamiento" (the data sandbox): a manager
// taps the button, confirms, and the shell enters training mode — same screens,
// but every data edit lives in a cloned in-memory copy of the seeds and is
// discarded on exit (see lib/training-data.ts + features/training/*).

import { useState } from 'react'
import { ShieldCheck } from 'lucide-react'

import { requestManagerPin } from '../features/security/pin-gate-store.js'
import {
  GATED_ACTION_LABEL, DISCOUNT_GATE_PERCENT, DISCOUNT_GATE_AMOUNT_CENTS,
  CASH_VARIANCE_GATE_CENTS, PIN_GATE_DEMO_MODE, DEMO_MANAGER_PIN,
} from '../lib/security-policy.js'
import { formatCents } from '../lib/money.js'
import { useTrainingModeContext } from '../lib/training-mode.js'
import { t } from '../i18n/es-MX.js'
import { useConfirm } from '../components/ConfirmDialog.js'

export default function ManagerOverridePage() {
  const [result, setResult] = useState<null | 'ok' | 'cancel'>(null)
  const { training, setTrainingMode } = useTrainingModeContext()
  const confirm = useConfirm()

  async function testPin() {
    setResult(null)
    const { approved } = await requestManagerPin('order.priceOverride')
    setResult(approved ? 'ok' : 'cancel')
  }

  async function enterTraining() {
    if (training) return
    if (!(await confirm({ title: 'Modo entrenamiento', message: t('training.confirmEnter'), confirmLabel: 'Entrar' }))) return
    setTrainingMode(true)
  }

  return (
    <div className="dhk8-override-root">
      <h1 className="dhk8-override-title">Autorización de gerente</h1>
      <p className="dhk8-override-sub">
        Estas acciones piden PIN de gerente antes de aplicarse. El control ocurre directo
        en cada pantalla; aquí puedes consultar la política y verificar tu PIN.
      </p>

      <ul className="dhk8-override-list">
        {(Object.keys(GATED_ACTION_LABEL) as (keyof typeof GATED_ACTION_LABEL)[]).map((k) => (
          <li key={k} className="dhk8-override-item">{GATED_ACTION_LABEL[k]}</li>
        ))}
      </ul>

      <div className="dhk8-override-thresholds">
        <div>Descuento que requiere PIN: &gt; {DISCOUNT_GATE_PERCENT}% o &gt; {formatCents(DISCOUNT_GATE_AMOUNT_CENTS)}</div>
        <div>Diferencia de caja que requiere PIN al cerrar: &gt; {formatCents(CASH_VARIANCE_GATE_CENTS)}</div>
        {PIN_GATE_DEMO_MODE && <div>PIN de gerente (demo): {DEMO_MANAGER_PIN}</div>}
      </div>

      <button type="button" className="dhk8-override-btn" onClick={() => void testPin()}>
        <ShieldCheck size={16} aria-hidden /> Verificar PIN de gerente
      </button>
      {result === 'ok' && <p className="dhk8-override-ok" role="status">PIN verificado.</p>}
      {result === 'cancel' && <p className="dhk8-override-cancel" role="status">Verificación cancelada.</p>}

      <section className="dhk8-override-training">
        <h2 className="dhk8-override-training__title">{t('training.enter')}</h2>
        <p className="dhk8-override-training__desc">{t('training.enterDesc')}</p>
        <button type="button" className="dhk8-override-training__btn" onClick={enterTraining} disabled={training}>
          {training ? t('training.bannerText') : t('training.enter')}
        </button>
      </section>

      <style>{`
        .dhk8-override-root { display:flex; flex-direction:column; gap:var(--pos-space-4); max-width:480px; }
        .dhk8-override-title { font-family:var(--font-display, system-ui); font-size:var(--pos-text-2xl); font-weight:var(--pos-weight-semibold); margin:0; color:var(--pos-fg); }
        .dhk8-override-sub { font-size:var(--pos-text-sm); color:var(--pos-fg-tertiary); margin:0; line-height:1.45; }
        .dhk8-override-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:var(--pos-space-1); }
        .dhk8-override-item { font-size:var(--pos-text-sm); color:var(--pos-fg-secondary); padding:var(--pos-space-2) var(--pos-space-3); border:1px solid var(--pos-separator); border-radius:var(--pos-radius-md); background:var(--pos-surface); }
        .dhk8-override-thresholds { display:flex; flex-direction:column; gap:var(--pos-space-1); font-size:var(--pos-text-xs); color:var(--pos-fg-tertiary); font-variant-numeric:tabular-nums; padding:var(--pos-space-3); border:1px solid var(--pos-separator); border-radius:var(--pos-radius-md); background:var(--pos-elevated); }
        .dhk8-override-btn { display:inline-flex; align-items:center; gap:var(--pos-space-2); align-self:flex-start; min-height:var(--pos-touch-target); padding:0 var(--pos-space-4); border:1px solid var(--pos-tint); border-radius:var(--pos-radius-md); background:var(--pos-tint); color:#fff; font:inherit; font-size:var(--pos-text-md); font-weight:var(--pos-weight-semibold); cursor:pointer; box-shadow:var(--pos-shadow-1); transition:filter var(--pos-duration-fast) ease; }
        .dhk8-override-btn:hover { filter:brightness(1.06); }
        .dhk8-override-ok { margin:0; font-size:var(--pos-text-sm); color:var(--pos-success); }
        .dhk8-override-cancel { margin:0; font-size:var(--pos-text-sm); color:var(--pos-fg-tertiary); }
        .dhk8-override-training { display:flex; flex-direction:column; gap:var(--pos-space-2); padding:var(--pos-space-4); border:1px solid var(--pos-separator); border-radius:var(--pos-radius-lg); background:var(--pos-surface); }
        .dhk8-override-training__title { font-family:var(--font-display, system-ui); font-size:var(--pos-text-lg); font-weight:var(--pos-weight-semibold); margin:0; color:var(--pos-fg); }
        .dhk8-override-training__desc { font-size:var(--pos-text-sm); color:var(--pos-fg-tertiary); margin:0; }
        .dhk8-override-training__btn {
          align-self:flex-start; margin-top:var(--pos-space-2);
          appearance:none; border:none; cursor:pointer;
          min-height:44px; padding:0 var(--pos-space-4);
          border-radius:var(--pos-radius-md);
          background:var(--pos-tint); color:#fff;
          font:inherit; font-size:var(--pos-text-sm); font-weight:var(--pos-weight-semibold);
          transition:filter var(--pos-duration-fast) ease;
        }
        .dhk8-override-training__btn:hover { filter:brightness(1.06); }
        .dhk8-override-training__btn:active { filter:brightness(0.94); }
        .dhk8-override-training__btn:disabled { opacity:.55; cursor:default; filter:none; }
      `}</style>
    </div>
  )
}
